import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import CtaBand from "@/components/CtaBand";
import { ARTICLES } from "@/data/articles";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const Article = () => {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <Navigate to="/guides" replace />;

  return (
    <>
      <Seo
        title={`${article.title} | ATLAS THD`}
        description={article.description}
        breadcrumbs={[
          { label: "Accueil", to: "/" },
          { label: "Guides", to: "/guides" },
          { label: article.title },
        ]}
      />

      <article data-testid="article-page">
        <section className="border-b border-line bg-cloud">
          <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:py-20 lg:px-8">
            <Breadcrumb
              items={[
                { label: "Accueil", to: "/" },
                { label: "Guides", to: "/guides" },
                { label: article.title },
              ]}
            />
            <motion.h1
              {...fadeUp}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-6 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl lg:text-5xl"
            >
              {article.title}
            </motion.h1>
            <motion.p
              {...fadeUp}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 text-lg leading-relaxed text-steel"
            >
              {article.intro}
            </motion.p>
          </div>
        </section>

        <div className="bg-white py-16">
          <div className="mx-auto max-w-4xl space-y-12 px-4 sm:px-6 lg:px-8">
            {article.sections.map((s, i) => (
              <motion.section key={s.h2} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.05 }} data-testid={`article-section-${i}`}>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-midnight">
                  {s.h2}
                </h2>
                {s.paragraphs.map((p, j) => (
                  <p key={j} className="mt-4 leading-relaxed text-steel">
                    {p}
                  </p>
                ))}
              </motion.section>
            ))}
            <motion.div {...fadeUp} transition={{ duration: 0.4 }} className="flex flex-col items-start justify-between gap-4 border border-line bg-cloud p-6 sm:flex-row sm:items-center">
              <p className="text-sm font-medium text-midnight">
                Un projet de raccordement, de maintenance ou de contrôle ?
              </p>
              <Link
                to="/contact"
                data-testid="article-cta"
                className="group inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors hover:text-terrain"
              >
                Parlez-nous de votre besoin
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </motion.div>
            <Link to="/guides" data-testid="article-back" className="inline-flex items-center gap-2 text-sm font-medium text-steel transition-colors hover:text-midnight">
              <ArrowLeft className="h-4 w-4" />
              Tous les guides
            </Link>
          </div>
        </div>
      </article>

      <CtaBand />
    </>
  );
};

export default Article;
